import './TefComsLoader';
import './TefComsSaveButton';
import './TefComsCancelButton';
import './TefComsButton';

import { config } from '@mfe/config';
import { configStore, consentStore } from '@mfe/store';
import { DEFAULT_MSISDN_FILTER } from '@mfe/store/ConsentStore';
import Api4pClient from '@mfec/clients/Api4pClient';
import { transformConsents4Save } from '@mfec/clients/transformers/postConsentsUpdateList';
import { StandardMicrofrontendMixin } from '@mfec/lit/mixins/StandardMicrofrontendMixin';
import { PurposeType } from '@mfec/types/enum';
import { App, Purpose } from '@mfec/types/model';
import { assert } from '@mfec/utils/assert';
import { formatMsisdn } from '@mfec/utils/MobilePhoneNumber.ts';
import type { CSSResultGroup } from 'lit';
import {
  customElement, property, query,
} from 'lit/decorators.js';

import { BaseComponent } from './BaseComponent';
import styles from './TefComs4cs4p.css';

@customElement('tef-coms4cs4p')
export class TefComs4cs4p extends StandardMicrofrontendMixin(BaseComponent, configStore, config) {
  // STATIC style imported above (for dynamic values use --css-vars)
  static readonly styles = styles as unknown as CSSResultGroup;

  // These properties are controllable from the outside via HTML attributes eg. on the components tag
  @property({ type: String, attribute: 'api-url' }) apiUrl! : string;
  @property({ type: String, attribute: 'sp-id' }) spId!: string;
  @property({ type: String, attribute: 'lc-id' }) lcId!: string;
  @property({ type: String, attribute: 'fetch' }) fetch?: string | undefined;

  @query('tef-shop-switcher-dropdown')
    // @ts-expect-error "lit syntax, works just fine"
    tefShopSwitcherDropdownEl : HTMLSelectElement & { selectedOption: string };

  client: Api4pClient | undefined;
  #isInitDone : boolean = false;

  constructor() {
    super();
    this.setupStore(consentStore);
  }

  // Called when the DOM has been updated (See https://lit.dev/docs/components/lifecycle/#updated)
  override updated(changedProperties: Map<PropertyKey, unknown>) {
    this.updateConfigStoreValues(changedProperties);

    // The component is usable when uncloaked
    if (!this.#isInitDone && !this.cloak) {
      this.triggerEvent({ name: 'load/READY', level: 'info' });
      this.#isInitDone = true;
    }
  }

  override render() {
    return this.getTemplateFn('tefComs4cs4p')();
  }

  // Invoked when a component is added to the document's DOM.
  override async connectedCallback() {
    try {
      // Required attributes must be present on the element:
      assert(!!this.spId, 'missing required html attribute "sp-id"');
      assert(!!this.lcId, 'missing required html attribute "lc-id"');
      assert(!!this.apiUrl, 'missing required html attribute "api-url"');

      // Call super only AFTER validating all required attributes!
      super.connectedCallback();
      await this.setup();
    } catch (error) {
      this.triggerEvent({ name: 'load/ERROR', level: 'error', error: error as Error });
      this.#isInitDone = true;
      throw error;
    }
  }

  // General comp setup
  async setup() {
    // Load consents
    this.client = new Api4pClient({
      apiUrl: this.apiUrl, accessToken: this.accessToken, altFetchMethodName: this.fetch, traceid: this.traceid,
    });

    try {
      consentStore.initialConsents = this.setDisabledForAppIfConsentIsRevoked(
        await this.client.getConsents(configStore.spId, configStore.lcId),
      );
    } catch (error) {
      this.triggerEvent({ name: 'consents/ERROR', level: 'error', error: error as Error });
      throw error;
    }

    // Copy loaded consents as working copy
    consentStore.actualConsents = structuredClone(consentStore.initialConsents);

    if (consentStore.initialConsents.length === 0) {
      this.triggerEvent({ name: 'consents/EMPTY', level: 'info' });
    } else {
      this.triggerEvent({ name: 'consents/LOAD', level: 'info' });
    }
  }

  // Disable the checkboxes for already revoked consents.
  // For this we set the "disabled" property on each app
  setDisabledForAppIfConsentIsRevoked(initialConsents : Purpose[]) : Purpose[] {
    initialConsents.forEach((purpose) => {
      if (purpose.purposeType === PurposeType.identifier_bound) {
        purpose.subscriptions!.forEach((subscription) => {
          subscription.apps.forEach((app) => {
            app.disabled = !app.granted; // eslint-disable-line no-param-reassign
          });
        });
      }

      // For user_bound purposes the apps are on the purpose itself
      if (purpose.purposeType === PurposeType.user_bound) {
        purpose.apps!.forEach((app) => {
          app.disabled = !app.granted; // eslint-disable-line no-param-reassign
        });
      }
    });

    return initialConsents;
  }

  // Saves the current consent setting via api4p "postConsentsUpdateList"
  async save() {
    if (consentStore.actualConsents.length === 0) return;
    const { tenant } = consentStore.actualConsents[0];
    const consentUpdateList = transformConsents4Save(consentStore.actualConsents);
    if (consentUpdateList.length === 0) return;

    try {
      await this.client?.postConsentsUpdateList(tenant, consentUpdateList, configStore.spId, configStore.lcId);

      // Success!
      this.triggerEvent({ name: 'consents/SAVE', level: 'info' });

      // Reload data
      await this.setup();
    } catch (error) {
      this.triggerEvent({ name: 'consents/ERROR', level: 'error', error: error as Error });
      throw error;
    }
  }

  get dropdownOptions() {
    const dropdownOptions = [{ text: DEFAULT_MSISDN_FILTER, value: DEFAULT_MSISDN_FILTER }];
    consentStore.initialConsents?.filter((purpose) => purpose.purposeType === PurposeType.identifier_bound)
      .flatMap((purpose) => purpose.subscriptions)
      .map((subscription) => subscription!.msisdn)
      .forEach((msisdn) => {
        if (!dropdownOptions.some((option) => option.value === msisdn)) {
          dropdownOptions.push({ text: formatMsisdn(msisdn), value: msisdn });
        }
      });
    return JSON.stringify(dropdownOptions).replace(/\s/g, '\u00A0'); // bugfix CoMS 391: add nonbreakable spaces
  }

  // Filtered actual consents. This is the only data source that should be used in templates!
  get consents() {
    if (consentStore.chosenMsisdn === DEFAULT_MSISDN_FILTER) {
      return consentStore.actualConsents;
    }
    const displayPurposes: Purpose[] = [];

    consentStore.actualConsents.forEach((purpose) => {
      if (purpose.purposeType === PurposeType.identifier_bound && purpose.subscriptions!.find((s) => s.msisdn === consentStore.chosenMsisdn)) {
        displayPurposes.push({
          ...purpose,
          subscriptions: purpose.subscriptions!.filter((s) => s.msisdn === consentStore.chosenMsisdn),
        });
      }

      // When no filter is set, also add the user_bound purposes
      if (purpose.purposeType === PurposeType.user_bound && !consentStore.chosenMsisdn) {
        displayPurposes.push(purpose);
      }
    });

    return displayPurposes;
  }

  handleFilterMsisdn(this: TefComs4cs4p, event: CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    let msisdn = event.detail.value as string; // dropdown event
    if (!msisdn) { // msisdn link event
      msisdn = (event.target as HTMLElement).dataset.subscriptionId!;
    }
    this.updateDropdownValue(msisdn);
    consentStore.chosenMsisdn = msisdn;
  }

  handleUpdateConsentForApp(app: App) {
    // eslint-disable-next-line no-param-reassign
    app.granted = !app.granted;
    // Trigger reactivity
    consentStore.actualConsents = [...consentStore.actualConsents];
  }

  updateDropdownValue(msisdn: string) {
    if (this.tefShopSwitcherDropdownEl) {
      const el = this.tefShopSwitcherDropdownEl;
      el.selectedOption = msisdn;
      const dropdownValueEl = el.shadowRoot?.querySelector('.sd-dropdown-value');
      dropdownValueEl!.innerHTML = `${formatMsisdn(msisdn)} <tef-icon icon="chevron-down" type="functional"></tef-icon>`;
    }
  }

  withdrawConsentForAllMsisdnsForPurpose(this: TefComs4cs4p, event: Event) {
    // The data-purpose-id attribute is on the <tef-coms-button>, so we use currentTarget instead of target
    const { purposeId } = (event.currentTarget as HTMLElement).dataset;
    if (!purposeId) return;
    const purpose = consentStore.actualConsents.find((c) => c.purposeId === purposeId);

    if (purpose) {
      this.withdrawConsentForPurpose(purpose);
    }
  }

  withdrawAllConsentsForAllPurposes(this: TefComs4cs4p) {
    consentStore.actualConsents.forEach((purpose) => {
      this.withdrawConsentForPurpose(purpose);
    });
  }

  revertToInitialConsents(this: TefComs4cs4p) {
    consentStore.actualConsents = structuredClone(consentStore.initialConsents);
  }

  withdrawConsentForPurpose(purpose: Purpose) {
    if (purpose.purposeType === PurposeType.identifier_bound) {
      purpose.subscriptions!.forEach((msisdn) => {
        msisdn.apps.forEach((app) => {
          if (app.granted) {
            app.granted = false;// eslint-disable-line no-param-reassign
            app.disabled = true; // eslint-disable-line no-param-reassign
          }
        });
      });
    }

    if (purpose.purposeType === PurposeType.user_bound) {
      purpose.apps!.forEach((app) => {
        if (app.granted) {
          app.granted = false;// eslint-disable-line no-param-reassign
          app.disabled = true; // eslint-disable-line no-param-reassign
        }
      });
    }

    // Trigger reactivity
    consentStore.actualConsents = [...consentStore.actualConsents];
  }
}
